import { ArrowLeft } from 'lucide-react';
import { useEffect, useCallback } from 'react';
import { useParams, useNavigate, useBlocker } from 'react-router-dom';

import { ConclusionPanel } from './components/conclusion-panel';
import { DecisionPanel } from './components/decision-panel';
import { FeedbackPanel, classifyOutcome } from './components/feedback-panel';
import { NarrativePanel } from './components/narrative-panel';
import { ScenarioProgressBar } from './components/scenario-progress-bar';

import type { ScenarioOption } from '@metaonia/api-client';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCompleteModule } from '@/hooks/use-complete-module';
import { markModuleStarted } from '@/hooks/use-started-modules';
import { useTrainingModule, useTrainingModules } from '@/hooks/use-training-modules';
import { useScenarioStore } from '@/stores/scenario-store';

export function ScenarioPlayerPage() {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const { data: module, isLoading } = useTrainingModule(moduleId ?? '');
  const { data: allModules } = useTrainingModules();
  const completeMutation = useCompleteModule(moduleId ?? '');

  const {
    currentNodeId,
    choices,
    showFeedback,
    lastChoiceIndex,
    isComplete,
    startScenario,
    makeChoice,
    advanceTo,
    completeScenario,
    reset,
  } = useScenarioStore();

  // Initialize scenario when module loads
  useEffect(() => {
    if (module?.scenario_data && !currentNodeId) {
      markModuleStarted(module.id);
      startScenario(module.id, module.scenario_data.start_node);
    }
  }, [module, currentNodeId, startScenario]);

  // beforeunload warning
  useEffect(() => {
    if (choices.length > 0 && !isComplete) {
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault();
      };
      window.addEventListener('beforeunload', handler);
      return () => window.removeEventListener('beforeunload', handler);
    }
    return undefined;
  }, [choices.length, isComplete]);

  // Block in-app navigation (sidebar links, etc.) while scenario is in progress
  const blocker = useBlocker(choices.length > 0 && !isComplete);
  useEffect(() => {
    if (blocker.state === 'blocked') {
      const leave = window.confirm('You have an incomplete scenario. Are you sure you want to leave? Your progress will be lost.');
      if (leave) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);

  const scenarioData = module?.scenario_data;
  const currentNode =
    scenarioData && currentNodeId ? scenarioData.nodes[currentNodeId] : undefined;
  const hasOptions = currentNode?.options && currentNode.options.length > 0;

  // Count total decision nodes (nodes with options)
  const totalDecisions = scenarioData
    ? Object.values(scenarioData.nodes).filter(
        (n) => n.options && n.options.length > 0,
      ).length
    : 0;

  const handleChoice = useCallback(
    (optionIndex: number) => {
      if (!currentNode?.options || !currentNodeId) return;
      const option = currentNode.options[optionIndex];
      if (!option) return;
      const maxScore = Math.max(...currentNode.options.map((o) => o.score));
      makeChoice(currentNodeId, optionIndex, option.score, maxScore);
    },
    [currentNode, currentNodeId, makeChoice],
  );

  const handleContinue = useCallback(() => {
    if (!currentNode?.options || lastChoiceIndex === null) return;
    const option = currentNode.options[lastChoiceIndex];
    if (!option) return;

    const nextNode = scenarioData?.nodes[option.next_node];
    if (!nextNode || !nextNode.options || nextNode.options.length === 0) {
      completeScenario();
    } else {
      advanceTo(option.next_node);
    }
  }, [currentNode, lastChoiceIndex, scenarioData, advanceTo, completeScenario]);

  const handleComplete = useCallback(async () => {
    const totalPoints = choices.reduce((sum, c) => sum + c.score, 0);
    const maxPoints = choices.reduce((sum, c) => sum + c.maxScore, 0);
    const percentage = maxPoints > 0 ? (totalPoints / maxPoints) * 100 : 0;

    try {
      await completeMutation.mutateAsync({
        score: {
          total_points: totalPoints,
          max_points: maxPoints,
          percentage,
          choices: choices.map((c) => ({
            node_id: c.nodeId,
            selected_option: c.optionIndex,
            points: c.score,
          })),
        },
      });
      navigate('/progress');
    } catch {
      // Error state handled via completeMutation.isError in the UI
    }
  }, [choices, completeMutation, navigate]);

  const handleTryAgain = useCallback(() => {
    completeMutation.reset();
    if (module && scenarioData) {
      startScenario(module.id, scenarioData.start_node);
    }
  }, [module, scenarioData, startScenario, completeMutation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => reset();
  }, [reset]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (!module || !scenarioData || !currentNode) return null;

  const nextModule = allModules
    ?.filter((m) => m.order_index > module.order_index)
    .sort((a, b) => a.order_index - b.order_index)[0];

  const selectedOption =
    showFeedback && lastChoiceIndex !== null
      ? (currentNode.options?.[lastChoiceIndex] as ScenarioOption | undefined)
      : undefined;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" aria-label="Back to training" onClick={() => navigate('/training')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold">{module.title}</h1>
      </div>

      <ScenarioProgressBar current={choices.length} total={totalDecisions} />

      {isComplete ? (
        <ConclusionPanel
          choices={choices.map((c) => ({
            node_id: c.nodeId,
            selected_option: c.optionIndex,
            points: c.score,
            max_points: c.maxScore,
          }))}
          totalPoints={choices.reduce((sum, c) => sum + c.score, 0)}
          maxPoints={choices.reduce((sum, c) => sum + c.maxScore, 0)}
          onComplete={handleComplete}
          onTryAgain={handleTryAgain}
          isCompleting={completeMutation.isPending}
          error={completeMutation.isError ? 'Failed to save completion. Please try again.' : undefined}
          nextModuleId={nextModule?.id}
        />
      ) : (
        <>
          <NarrativePanel prompt={currentNode.prompt} />
          {hasOptions && !showFeedback && (
            <DecisionPanel options={currentNode.options!} onChoose={handleChoice} />
          )}
          {showFeedback && selectedOption && (
            <FeedbackPanel
              outcome={classifyOutcome(selectedOption.score, currentNode.options!)}
              feedback={selectedOption.feedback ?? 'Consider the impact of your choice.'}
              learningPoints={selectedOption.learning_points}
              onContinue={handleContinue}
            />
          )}
        </>
      )}

    </div>
  );
}
